import React, { useContext, useMemo } from "react";
import useImageStyle from '../../core/hooks/useImageStyle';
import useBaseStyle from '../../core/hooks/useBaseStyle';
import { getBackgroundPatternClass } from '../../core/utils/backgroundPatternUtils';
import { getFontFamilyClass, getFontFamilyStyle } from '../../core/utils/characterStyleUtils';
import FooterForm from "./FooterForm";
import Background from "../components/Background";
import ContainerEffectOverlay from "../components/ContainerEffectOverlay";
import useContainerWidth from "../../core/hooks/useContainerWidth";
import EditableElement from "../../editor/components/EditableElement";
import { NavBarLogo } from "../../design-system";
import { EditContext } from "../../editor/context/EditContext";
import { APP_URL_WWW } from "../../core/utils/config";

const FOOTER_STYLES = {
  container: "relative z-20 py-8 mt-auto",
  content: "relative z-10 container mx-auto px-4",
  mobileContainer: "w-full mb-8 flex flex-col items-center",
  desktopContainer: "w-full mb-8 relative min-h-[200px]",
  copyright: "text-sm mb-4",
  desktopCopyright: "text-sm absolute top-0 left-0",
  formContainer: "mb-4",
  desktopFormContainer: "mt-4 flex justify-center",
  buttonMobile:
    "bg-white border border-gray-300 rounded-full p-2 shadow-sm hover:shadow-md transition-all duration-300 hover:brightness-125",
  buttonDesktop:
    "absolute top-0 right-0 bg-white hover:brightness-125 border border-gray-300 rounded-full p-2 shadow-sm hover:shadow-md transition-all duration-300",
};

const FOOTER_CONFIG = {
  minHeight: "500px",
};

const isTailwindToken = (value) => {
  if (!value || typeof value !== "string") return false;
  return value.includes("-") && !value.startsWith("#");
};

const resolveTextColorProps = (value) => {
  if (!value) return { className: "", style: {} };
  if (isTailwindToken(value)) return { className: `text-${value}`, style: {} };
  return { className: "", style: { color: value } };
};

const BackToTopButton = ({ highlightColor, onClick, className }) => {
  const colorProps = resolveTextColorProps(highlightColor);
  return (
    <button onClick={onClick} className={`${className} ${colorProps.className}`} style={colorProps.style}>
    <span className="sr-only">Back to Top</span>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 15l7-7 7 7"
      />
    </svg>
  </button>
  );
};

const useBackgroundSetup = (footer) => {
  const bgImageObj = footer.background?.background_image;
  const { styleClasses: imageStyleClasses, inlineStyles: imageInlineStyles } =
    useImageStyle(bgImageObj);
  const showImage = footer.background?.show_image;
  const bgImageUrl = showImage && bgImageObj?.image ? bgImageObj.image : null;
  const bgPattern = footer.background?.background_pattern;
  const {
    colorClass: bgPatternClass = "",
    colorStyle: bgPatternStyle = {},
    jsPatternLayer: bgPatternJsLayer = null,
  } = getBackgroundPatternClass(bgPattern);

  return {
    bgImageUrl,
    imageStyleClasses,
    imageInlineStyles,
    bgPatternClass,
    bgPatternStyle,
    bgPatternJsLayer,
  };
};

const MobileFooter = ({ footer, scrollToTop, isMobile, containerId, disableSubmit, currentYear }) => {
  const copyrightColor = footer.copyright_text_color || footer.highlight_color;
  const copyrightColorProps = resolveTextColorProps(copyrightColor);
  const fontFamilyClass = getFontFamilyClass(footer.font_family);
  const fontFamilyStyle = getFontFamilyStyle(footer.font_family);
  return (
    <div className={FOOTER_STYLES.mobileContainer}>
      <div className={FOOTER_STYLES.copyright}>
        {footer.show_our_logo !== false && (
          <div className="mb-2">
            <span className="bg-white text-black rounded-full px-2 py-0.5 font-thin">© {currentYear} Vitae AI, LLC</span>
          </div>
        )}
        <div
          className={`${fontFamilyClass} ${copyrightColorProps.className}`}
          style={{ ...copyrightColorProps.style, ...fontFamilyStyle }}
        >
          {footer.copyright_text}
        </div>
      </div>
    {footer.show_our_logo !== false && (
      <div className="mb-4 flex justify-center">
        <a 
          href={APP_URL_WWW} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <NavBarLogo 
            iconSize="w-4 h-4" 
            textSize="text-sm"
          />
        </a>
      </div>
    )}
    {footer.show_form_to_send_message && (
      <div className={FOOTER_STYLES.formContainer}>
        <FooterForm footer={footer} isMobile={isMobile} containerId={containerId} disableSubmit={disableSubmit} />
      </div>
    )}
    {footer.show_back_to_top && (
      <BackToTopButton
        highlightColor={footer.highlight_color}
        onClick={scrollToTop}
        className={FOOTER_STYLES.buttonMobile}
      />
    )}
    </div>
  );
};

const DesktopFooter = ({ footer, scrollToTop, isMobile, containerId, disableSubmit, currentYear }) => {
  const copyrightColor = footer.copyright_text_color || footer.highlight_color;
  const copyrightColorProps = resolveTextColorProps(copyrightColor);
  const fontFamilyClass = getFontFamilyClass(footer.font_family);
  const fontFamilyStyle = getFontFamilyStyle(footer.font_family);
  return (
    <div className={FOOTER_STYLES.desktopContainer}>
      <div className={FOOTER_STYLES.desktopCopyright}>
        {footer.show_our_logo !== false && (
          <div className="mb-2">
            <span className="bg-white text-black rounded-full px-2 py-0.5 font-thin">© {currentYear} Vitae AI, LLC</span>
          </div>
        )}
        <div
          className={`${fontFamilyClass} ${copyrightColorProps.className}`}
          style={{ ...copyrightColorProps.style, ...fontFamilyStyle }}
        >
          {footer.copyright_text}
        </div>
      </div>
    {footer.show_our_logo !== false && (
      <div className="absolute top-0 right-16">
        <a 
          href={APP_URL_WWW} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <NavBarLogo 
            iconSize="w-4 h-4" 
            textSize="text-sm"
          />
        </a>
      </div>
    )}
    {footer.show_back_to_top && (
      <BackToTopButton
        highlightColor={footer.highlight_color}
        onClick={scrollToTop}
        className={FOOTER_STYLES.buttonDesktop}
      />
    )}
    {footer.show_form_to_send_message && (
      <div className={FOOTER_STYLES.desktopFormContainer}>
        <FooterForm footer={footer} isMobile={isMobile} containerId={containerId} disableSubmit={disableSubmit} />
      </div>
    )}
    </div>
  );
};

const Footer = ({ footer = {}, containerId }) => {
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;
  const currentYear = new Date().getFullYear();

  // Keep UI reactive while editing footer
  const currentFooter = useMemo(() => {
    const sel = editContext?.selectedElement;
    if (!sel || sel.type !== "footer" || sel.id !== footer?.id) return footer;

    const deepMerge = (target, source) => {
      if (!target) return source;
      const result = Array.isArray(target) ? [...target] : { ...target };
      for (const key in source) {
        const sv = source[key];
        if (sv && typeof sv === "object" && !Array.isArray(sv)) {
          result[key] = deepMerge(target[key] || {}, sv);
        } else {
          result[key] = sv;
        }
      }
      return result;
    };

    return deepMerge(footer, sel);
  }, [editContext?.selectedElement, footer]);

  const backgroundSetup = useBackgroundSetup(currentFooter);

  const [footerRef, isMobile] = useContainerWidth(1024);
  const { styleClasses: footerBaseStyleClasses, inlineStyles: footerBaseInlineStyles } = useBaseStyle(currentFooter);

  const scrollToTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // If no footer exists, return null
  if (!currentFooter || Object.keys(currentFooter).length === 0) {
    return null;
  }

  const footerContent = (
    <footer
      ref={footerRef}
      className={`${FOOTER_STYLES.container} ${footerBaseStyleClasses}`.trim()}
      style={{ minHeight: FOOTER_CONFIG.minHeight, ...footerBaseInlineStyles }}
    >
      <Background
        bgImage={backgroundSetup.bgImageUrl}
        imageStyleClasses={backgroundSetup.imageStyleClasses}
        imageInlineStyles={backgroundSetup.imageInlineStyles}
        colorClass={backgroundSetup.bgPatternClass}
        colorStyle={backgroundSetup.bgPatternStyle}
        jsPatternLayer={backgroundSetup.bgPatternJsLayer}
      />
      <ContainerEffectOverlay data={currentFooter} />

      <div className={FOOTER_STYLES.content}>
        {isMobile ? (
          <MobileFooter
            footer={currentFooter}
            scrollToTop={scrollToTop}
            isMobile={isMobile}
            containerId={containerId}
            disableSubmit={isEditMode}
            currentYear={currentYear}
          />
        ) : (
          <DesktopFooter
            footer={currentFooter}
            scrollToTop={scrollToTop}
            isMobile={isMobile}
            containerId={containerId}
            disableSubmit={isEditMode}
            currentYear={currentYear}
          />
        )}
      </div>
    </footer>
  );

  return (
    <EditableElement element={currentFooter}>
      {footerContent}
    </EditableElement>
  );
};

export default Footer;
